/**
 * Converts a monetary value (e.g., dollars) into cents.
 * Handles potential floating-point precision issues by rounding to the nearest whole cent.
 *
 * @param {number} value - The monetary value (e.g., 200 for $200, 1.1 for $1.10).
 * @returns {number} The value in cents as an integer.
 *
 * @example
 * asCents(200);   // Returns 20000 (200 dollars to 20000 cents)
 * asCents(1.1);    // Returns 110 (1 dollar 10 cents to 110 cents)
 * asCents(20);     // Returns 2000 (20 dollars to 2000 cents)
 * asCents(1.05);   // Returns 105 (1 dollar 5 cents to 105 cents)
 * asCents(0.99);   // Returns 99 (99 cents to 99 cents)
 * asCents(1.123);  // Returns 112 (rounds to nearest cent)
 * asCents(-5.55);  // Returns -555
 * asCents(0.005);  // Returns 1 (rounds 0.5 up to 1)
 * asCents(0.004);  // Returns 0 (rounds 0.4 down to 0)
 */
export function asCents(value: number): number {
  // Multiply the value by 100 to shift the decimal two places to the right.
  // Use Math.round() to correctly handle any floating-point inaccuracies
  // and to ensure the result is a whole number (representing cents).
  return Math.round(value * 100);
}
